import { AbsgArrayHelper, LogService, SessionContextService } from '@absg/angular-common';
import { BaseService, EnvironmentService } from '@absg/nim-client-shared';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NIMGoogleTagMangerService extends BaseService {
  private hitCounter = 0;
  private dupPrintLabelId = 'printLabelButton';
  private dupRestockId = 'btnRestock';
  private dupRestockAllId = 'btnRestockAll';
  private backButton = 'backButton';
  private clickEventDictionary = new Map([
    ['addLabelNotesButton', 'Dispense Summary - Add Label Notes Button Click'],
    ['printCondensedLabelButton', 'Dispense Summary - Print Condensed Label Button Click'],
    ['printCombinedLabelButton', 'Dispense Summary - Print Combined Label Button Click'],
    ['#dispenseSummary ~ .nim__sidebar #printLabelButton,#dispenseSummary ~ .nim__sidebar #printLabelButton', 'Dispense Summary - Print Individual Labels Button Click'],
    ['printLabelButtonSummary', 'Dispense Summary - Print Individual Labels Button Click'],
    ['#patientHistory ~ .nim__sidebar #printLabelButton,#patientHistory ~ .nim__sidebar #printLabelButton *', 'Patient History - Print Combined Label Button Click'],
    ['printLabelButtonHistory', 'Patient History - Print Combined Label Button Click'],
    ['printCombinedLabelButton', 'Dispense Summary - Print Combined Label Button Click'],
    ['printLabelButtonIndividual', 'Patient History - Print Individual Label Button Click'],
    ['printLabelButtonCondensed', 'Patient History - Print Condensed Label Button Click'],
    ['printReceiptButton', 'Patient History - Print Receipt Button Click'],
    ['chkFilterIconsAndPillsFavorite', 'PO Favorites Filter Click'],
    ['chkFilterIconsAndPillsPOItem', 'PO Practice Owned Filter Click'],
    ['purchaseOrderTab', 'Purchase Order Tab Click'],
    ['btnStandardRestock', 'Restock by Tracking Number - Standard Restock Button Click'],
    ['btnViewPackingSlip', 'Restock by Tracking Number - View Packing Slip Button Click'],
    ['btnRestockRequest', 'Restock Calculator - Restock Button Click'],
    ['btnRestock', 'Standard Restock - Restock Button'],
    ['btnPackingSlipRestock', 'Packing Slip Detail - Restock Button Click'],
    ['btnRestockAll', 'Standard Restock - Restock All Button Click'],
    ['btnRestockAllPackingSlip', 'Packing Slip Detail - Restock All Button Click'],
    ['btnLogin', 'Log In Button Click'],
    ['btnRestockCancel', 'Restock Cancel Button Click'],
    ['divLogout', 'Log Out Button Click'],
    ['btnInventoryManagement', 'Inventory Management Button Click'],
    ['btnManagePositions', 'Manage Positions Button Click'],
    ['backButton', 'Back Button on'],
    ['btnAudit', 'Audit Button Click'],
    ['btnHelpCenter', 'Help Center Button Click'],
    ['btnPurchaseOrder', 'Purchase Order Button Click'],
    ['btnOk', 'OK Button Click'],
    ['divPracticeDomain', 'Practice Domain Click'],
    ['divPracticeNameAddress', 'Practice Address Click'],
    ['divStationNameBuildEnv', 'Station Build-Environment Click'],
    ['futureMedOrderOptionsPanel', 'Future Med Order Options Click'],
  ]);

  constructor(private environmentService: EnvironmentService,
    private stationInfoService: StationInfoService,
    private http: HttpClient,
    private zone: NgZone,
    loggingService: LogService,
    private sessionContext: SessionContextService,
  ) {
    super(loggingService);
    this.hitCounter = 0;
  }

  flushData(): void {
    if (!AbsgArrayHelper.isNil(window['dataLayer'])) {
      window['dataLayer'].splice(0);
    }
  }

  checkForGoogleTagItems(): void {
    const data = window['dataLayer'];
    let gtmUrl = this.getBaseCollectUrl();
    gtmUrl += '&en=page_view';
    AbsgArrayHelper.forEach(data, (info) => {
      if (info['event']?.includes('gtm.historyChange') && info['sent'] !== true) {
        gtmUrl = this.appendURLToGTM(info, gtmUrl, info['gtm.newUrl']);
        this.zone.run(() => {
          this.http.post(gtmUrl, null)
            .pipe(
              take(1),
              tap(() => {
                info['sent'] = true;
              }),
            ).subscribe();
        });
      }
    });
  }

  checkForClickEvents(): void {
    const data = window['dataLayer'];
    let gtmUrl = this.getBaseCollectUrl();
    AbsgArrayHelper.forEach(data, (info) => {
      const urlPath: string = !info['gtm.elementUrl'] ? window.location.href : info['gtm.elementUrl'];
      if (info['event']?.includes('gtm.click') && info['sent'] !== true) {
        const elementName = info['gtm.elementId'];
        if (!elementName) { return; }
        if (elementName === this.dupPrintLabelId) { // TODO: Look at making these ids unique
          if (urlPath.includes('dispense-summary')) {
            gtmUrl += `&en=${this.clickEventDictionary.get('printLabelButtonSummary')}`;
          } else {
            gtmUrl += `&en=${this.clickEventDictionary.get('printLabelButtonHistory')}`;
          }
        } else if (elementName === this.dupRestockId) { // TODO: Look at making these ids unique
          if (urlPath.toLocaleLowerCase().includes('standard')) {
            gtmUrl += `&en=${this.clickEventDictionary.get(elementName)}`;
          } else {
            gtmUrl += `&en=${this.clickEventDictionary.get('btnPackingSlipRestock')}`;
          }
        } else if (elementName === this.dupRestockAllId) { // TODO: Look at making these ids unique
          if (urlPath.toLocaleLowerCase().includes('standard')) {
            gtmUrl += `&en=${this.clickEventDictionary.get(elementName)}`;
          } else {
            gtmUrl += `&en=${this.clickEventDictionary.get('btnRestockAllPackingSlip')}`;
          }
        } else if (this.clickEventDictionary.has(elementName)) { // TODO: this should be remaining once duplicates are replaced
          gtmUrl += `&en=${this.clickEventDictionary.get(elementName)}`;
          if (this.backButton) {
            gtmUrl += ` ${urlPath.split('/')?.pop()}`;
          }
        } else {
          // If the tag trigger has not been added we want to start logging just tag Id until PR is made.
          gtmUrl += `&en=${elementName}`;
        }
        gtmUrl = this.appendURLToGTM(info, gtmUrl, urlPath);
        this.zone.run(() => {
          this.http.post(gtmUrl, null)
            .pipe(
              take(1),
              tap(() => {
                info['sent'] = true;
              }),
            ).subscribe();
        });
      }
    });
  }

  private appendURLToGTM(info, gtmUrl: string, path: string): string {
    const urlSplit = path?.split('app.asar');
    if (urlSplit.length > 1) {
      const urlResult = encodeURIComponent(urlSplit[1]);
      gtmUrl += `&dt=${urlResult}`; // document tag
      const urlResult2 = encodeURIComponent(`https://localhost${urlSplit[1]}`);
      gtmUrl += `&dl=${urlResult2}`; // document location (base url)
      const drUrl = encodeURIComponent(`https://localhost:44328${urlSplit[1]}`);
      gtmUrl += `&dr=${drUrl}`; // document referral (full location)
    } else {
      const baseUrl = path;
      gtmUrl += `&dt=${baseUrl}`;
      gtmUrl += `&dl=${baseUrl}`;
      gtmUrl += `&dr=${baseUrl}`;
    }
    gtmUrl += `&_s=${this.hitCounter}`;
    gtmUrl += `&_et=${info['gtm.uniqueEventId']}`;
    return gtmUrl;
  }

  private getBaseCollectUrl(): string {
    let gtmUrl = 'https://www.google-analytics.com/g/collect?v=2'; // current collect base url
    // https://www.thyngster.com/ga4-measurement-protocol-cheatsheet/# - variable definitions
    gtmUrl += `&tid=${this.getGoogleTagManagerApiId()}`; // usually from Cookie, but no local file cookies work
    gtmUrl += `&_p=${Math.floor(Math.random() * (2147483647 - 0 + 1) + 0)}`;
    const stationId = this.stationInfoService.getStationId();
    const idHash = this.getStationIdHash();
    gtmUrl += `&gtm=${stationId}`; // gtm hash from cookie, station id works for now
    gtmUrl += `&cid=${stationId}.${idHash}`; // hash value random generated from cookie, using station.hash(StationName) to make unique
    gtmUrl += `&sid=${idHash}`;

    // Remaining values are default based on station values
    gtmUrl += '&ul=en-us';
    gtmUrl += '&sr=1024x768';
    gtmUrl += '&uaa=x86';
    gtmUrl += '&uab=64';
    gtmUrl += '&uafvl=%2520Not%2520A%253BBrand%3B99.0.0.0%7CChromium%3B102.0.5005.167';
    gtmUrl += '&uamb=0';
    gtmUrl += '&uam=';
    gtmUrl += '&uap=Windows';
    gtmUrl += '&uapv=10.0.0';
    gtmUrl += '&uaw=0';
    gtmUrl += '&sct=1';
    gtmUrl += '&seg=1';
    // GTM unique parameters start has a prefix of 'ep.'
    const domain = this.sessionContext.getDomain().value;
    gtmUrl += `&ep.domain=${domain}`;
    const practice = this.sessionContext.getPracticeName().value;
    gtmUrl += `&ep.practice=${practice}`;
    return gtmUrl;
  }

  private getGoogleTagManagerApiId(): string {
    if (this.environmentService.isProduction()) {
      //prod GTM id
      return 'G-3D22RH8705';
    } else {
      //not prod GTM id
      return 'G-HWDB4V2Z99';
    }
  }

  private getStationIdHash(): number {
    const stationName = this.stationInfoService.getStationName();
    let hash = 0;
    if (stationName.length === 0) {
      return 0;
    }
    for (let i = 0; i < stationName.length; i++) {
      const chr = stationName.charCodeAt(i);
       
      hash = ((hash << 5) - hash) + chr;
       
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }
}
